import { useCallback, useEffect } from 'react';

import { useGetAlbum } from '@vimeo/gctl-api/hooks/useAlbum';
import { LayoutConfig } from '@vimeo/gctl-api/models/components/schemas/layout-config/LayoutConfig';
import { PlaybackConfig } from '@vimeo/gctl-api/models/components/schemas/playback-config/PlaybackConfig';
import { useViewer } from '@vimeo/viewer';
import { useIsClipView } from '@vimeo/viewer-showcase-ui/hooks/use-is-clip-view';
import { getConfigProperty, getShowcaseConfigValue } from '@vimeo/viewer-showcase-ui/utils/helper';
import { GridSize } from '@vimeo/viewer-showcase-ui/utils/types';

import { useAlbumStore } from '../state/albumStore';
import { ALBUM_SORT_MAP, DEFAULT_SORT } from '../utils/constants';
import { useShareLoopParams } from './use-share-loop-params';
import { useShowNewCustomizeControls } from './use-show-new-customize-controls';

const layoutMap: { [key: string]: string } = {
  grid: 'default',
  player: 'featured',
  live: 'live',
  playlist: 'playlist', // do not show header for this layout
};

export const useGetShowcase = () => {
  const {
    albumId,
    password,
    isShowcaseEmbedded,
    isOffsite,
    referrer,
    setNoOfVideos,
    setSeoAllowIndex,
    setAlbumPrivacy,
    setAlbumOwnerId,
  } = useAlbumStore((state) => ({
    albumId: state.albumId,
    password: state.hashedPass,
    isShowcaseEmbedded: state.isShowcaseEmbedded, // Comes from EmbedShowcaseContext https://github.com/vimeows/vimeo/blob/master/frontend/apps/embed/libs/showcase-layout/ShowcasePage.tsx#L34
    isOffsite: state.isOffsite,
    referrer: state.referrer,
    setNoOfVideos: state.setNoOfVideos,
    setSeoAllowIndex: state.setSeoAllowIndex,
    setAlbumPrivacy: state.setAlbumPrivacy,
    setAlbumOwnerId: state.setAlbumOwnerId,
  }));

  const isClipView = useIsClipView();
  const viewer = useViewer();
  const { data, isLoading } = useGetAlbum(() => {
    if (!viewer) return null;
    return {
      where: { albumId },
      select: [
        'privacy.view',
        'allowDownloads',
        'allowShare',
        'brandColor',
        'customLogo.sizes.link',
        'description',
        'domain',
        'embed',
        'hideNav',
        'hideUpcoming',
        'layout',
        'loop',
        'metadata.connections.videos.total',
        'name',
        'pictures.sizes.link',
        'sort',
        'theme',
        'url',
        'user.link',
        'user.name',
        'user.pictures.sizes.link',
        'user.uri',
        'webCustomLogo',
        'webBrandColor',
        'hasFeaturedContent',
        'seoAllowIndexed',
        'config',
      ],
      query: {
        isEmbed: isShowcaseEmbedded,
        referrer: referrer,
        ...(password && { password }),
      },
    };
  });

  const isNewCustomizeControls = useShowNewCustomizeControls();

  /**
   * Do not show vimeo navigation for any of the following cases -
   * 1. Viewing showcase on offsite
   * 2. Viewing showcase on embed page
   * 3. User has disabled vimeo navigation for this showcase
   * 4. Showcase layout type is playlist
   */
  // Layout schema only returns > layout: 'grid' | 'player'
  const isPlaylistLayout = data?.layout && layoutMap[data.layout] === 'playlist';

  const shouldHideNavigation = data?.hideNav || isOffsite || isShowcaseEmbedded || isPlaylistLayout;

  const sortConfig = data?.sort ? ALBUM_SORT_MAP[data.sort] : DEFAULT_SORT;

  const totalVideos = data?.metadata?.connections?.videos?.total || null;
  const seoAllowIndex = typeof data?.seoAllowIndexed === 'boolean' ? data?.seoAllowIndexed : null;
  const privacyView = data?.privacy?.view || null;
  const albumOwnerId = data?.user?.uri?.split('/').pop();
  const { hasShareLoopTrackingParams } = useShareLoopParams();
  const shareLoopParams = hasShareLoopTrackingParams ? '?share=copy&fl=sc&fe=fs' : '?share=copy';

  const {
    banner,
    layout: layoutConfig,
    playback: playbackConfig,
    showVideoDetails,
    showVideoPlayerCards,
  } = data?.config || {};

  // Helper for playback config extraction
  const getPlaybackConfig = useCallback(
    (key: keyof PlaybackConfig) =>
      getShowcaseConfigValue(isNewCustomizeControls, getConfigProperty(playbackConfig?.[key])),
    [isNewCustomizeControls, playbackConfig],
  );

  // Helper for playback and layout config extraction for video grid
  const getGridConfig = useCallback(
    (playbackKey: keyof PlaybackConfig, layoutKey: keyof LayoutConfig) => {
      const config = isClipView ? playbackConfig?.[playbackKey] : layoutConfig?.[layoutKey];
      return getShowcaseConfigValue(isNewCustomizeControls, getConfigProperty(config));
    },
    [isClipView, isNewCustomizeControls, layoutConfig, playbackConfig],
  );

  useEffect(() => {
    typeof totalVideos === 'number' && setNoOfVideos(totalVideos);
  }, [setNoOfVideos, totalVideos]);

  useEffect(() => {
    typeof seoAllowIndex === 'boolean' && setSeoAllowIndex(seoAllowIndex);
  }, [seoAllowIndex, setSeoAllowIndex]);

  useEffect(() => {
    privacyView && setAlbumPrivacy(privacyView);
  }, [privacyView, setAlbumPrivacy]);

  useEffect(() => {
    albumOwnerId && setAlbumOwnerId(Number(albumOwnerId) || 0);
  }, [albumOwnerId, setAlbumOwnerId]);

  const showPlayerCards = Boolean(getConfigProperty(showVideoPlayerCards)) || false;
  return {
    allowContinuousPlay: true,
    user: data?.user,
    layout: layoutMap[data?.layout || 'grid'],
    title: data?.name || '',
    description: data?.description || '',
    allowShare: data?.allowShare || false,
    customLogo: data?.webCustomLogo
      ? data?.customLogo?.sizes?.[data?.customLogo?.sizes?.length - 1].link
      : '',
    thumbnailUrl: data?.hasFeaturedContent // This will cause an issue with the legacy showcases transition as hasFeaturedContent is defaulted to false
      ? data?.pictures?.sizes?.[data?.pictures?.sizes?.length - 1].link
      : '',
    hideNav: shouldHideNavigation,
    colorMode: data?.theme === 'dark' ? 'dark' : 'light',
    isLoading: isLoading || !data, // show loading before data is fetched
    ...(data?.brandColor &&
      data?.webBrandColor && {
        brandColor: `#${data?.brandColor}`,
        playerAccentColor: data?.brandColor,
      }),
    theme: data?.theme,
    loop: data?.loop,
    // Do not use this `isEmpty` as the video total in metadata is not reliable. Use `total` from GET album videos call instead.
    isEmpty: data?.metadata?.connections?.videos?.total === 0,
    sortConfig,
    canEditShowcase: !!data?.embed && !isOffsite && !isShowcaseEmbedded,
    allowDownloads: data?.allowDownloads,
    hideUpcoming: data?.hideUpcoming,
    hasFeaturedContent: data?.hasFeaturedContent,
    shareLink:
      isOffsite && data?.domain
        ? `${data.domain}${shareLoopParams}`
        : `${viewer.vimeoHttpsUrl}/showcase/${data?.url ?? albumId}${shareLoopParams}`,
    gridSize: (isClipView
      ? getConfigProperty(playbackConfig?.playbackGridSize)
      : getConfigProperty(layoutConfig?.gridSize)) as GridSize,
    playback: {
      enableComments: getPlaybackConfig('enableComments'),
      enableLike: getPlaybackConfig('enableLike'),
      enableShare: getPlaybackConfig('enableShare'),
      showDateAdded: getPlaybackConfig('showPlaybackAddedDate'),
      showDescription: getPlaybackConfig('showPlaybackVideoDescription'),
      showProfileImage: getPlaybackConfig('showPlaybackProfileImage'),
      showProfileName: getPlaybackConfig('showPlaybackProfileName'),
      showTitle: getPlaybackConfig('showPlaybackVideoTitle'),
      showViews: getPlaybackConfig('showPlaybackViews'),
    },
    showGridProfileImage: getGridConfig('showPlaybackGridProfileImage', 'showGridProfileImage'),
    showGridProfileName: getGridConfig('showPlaybackGridProfileName', 'showGridProfileName'),
    showGridVideoTitle: getGridConfig('showPlaybackGridVideoTitle', 'showGridVideoTitles'),
    showVideoCount: getGridConfig('showPlaybackVideoCount', 'showGridVideoCount'),
    showGridVideoCard:
      isClipView && isNewCustomizeControls
        ? Boolean(getConfigProperty(playbackConfig?.showPlaybackGridVideoCard))
        : showPlayerCards,
    showVideoDetails: Boolean(getConfigProperty(showVideoDetails)),
    showBannerDescription: getShowcaseConfigValue(
      isNewCustomizeControls,
      getConfigProperty(banner?.showBannerDescription),
    ),
    showBannerTitle: getShowcaseConfigValue(
      isNewCustomizeControls,
      getConfigProperty(banner?.showBannerTitle),
    ),
    showSearchBar: getShowcaseConfigValue(
      isNewCustomizeControls,
      getConfigProperty(data?.config?.navigation?.showSearchBar),
    ),
    showBannerHeader: getShowcaseConfigValue(
      isNewCustomizeControls,
      getConfigProperty(banner?.showBannerHeader),
    ),
    showStartWatchingButton: getShowcaseConfigValue(
      isNewCustomizeControls,
      getConfigProperty(banner?.showStartWatchingButton),
    ),
    textCtaAlignment: isNewCustomizeControls
      ? String(getConfigProperty(data?.config?.textCtaAlignment))
      : 'left',
  };
};
